import React, { useState, useMemo } from 'react';
import {
  EyeIcon,
  DevicePhoneMobileIcon,
  ComputerDesktopIcon,
  DeviceTabletIcon
} from '@heroicons/react/24/outline';
import DynamicForm from '../DynamicForm/DynamicForm';

const VIEWPORT_SIZES = {
  desktop: { width: '100%', icon: ComputerDesktopIcon, label: 'Desktop' },
  tablet: { width: '768px', icon: DeviceTabletIcon, label: 'Tablet' },
  mobile: { width: '375px', icon: DevicePhoneMobileIcon, label: 'Mobile' }
};

const FormPreview = ({ schema, rules, settings }) => {
  const [viewport, setViewport] = useState('desktop');
  const [previewData, setPreviewData] = useState({});

  // Create a mock client ID for preview
  const mockClientId = 'preview-client';

  // Create form config for preview
  const formConfig = useMemo(() => ({
    id: 'preview-config',
    template_name: settings?.name || 'Form Preview',
    description: settings?.description || '',
    default_schema: schema,
    default_rules: rules || [],
    default_settings: settings || {}
  }), [schema, rules, settings]);

  // Mock handlers for preview
  const handlePreviewSubmit = (result) => {
    console.log('Preview form submitted:', result);
    // In preview mode, we don't actually submit
  };

  const handlePreviewError = (error) => {
    console.log('Preview form error:', error);
  };

  const viewportStyle = {
    width: VIEWPORT_SIZES[viewport].width,
    minHeight: '400px',
    maxWidth: '100%',
    margin: '0 auto',
    transition: 'width 0.3s ease'
  };

  if (!schema || !schema.fields || schema.fields.length === 0) {
    return (
      <div className="text-center py-12">
        <EyeIcon className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
          No fields to preview
        </h3>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Add some fields to the form to see a preview here.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Viewport Controls */}
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium text-gray-900 dark:text-white">
          Form Preview
        </h4>
        
        <div className="flex items-center space-x-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
          {Object.entries(VIEWPORT_SIZES).map(([key, config]) => {
            const Icon = config.icon;
            return (
              <button
                key={key}
                onClick={() => setViewport(key)}
                className={`
                  inline-flex items-center px-2 py-1 text-xs font-medium rounded-md transition-colors
                  ${viewport === key
                    ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                  }
                `}
                title={config.label}
              >
                <Icon className="h-3 w-3 mr-1" />
                {config.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Preview Container */}
      <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
        <div className="bg-gray-50 dark:bg-gray-800 px-4 py-2 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-2">
            <div className="flex space-x-1">
              <div className="w-3 h-3 bg-red-400 rounded-full"></div>
              <div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
              <div className="w-3 h-3 bg-green-400 rounded-full"></div>
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 ml-2">
              Form Preview - {VIEWPORT_SIZES[viewport].label}
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-900 p-4 overflow-x-auto">
          <div style={viewportStyle}>
            {/* Mock DynamicForm for Preview */}
            <div className="space-y-6">
              {/* Form Header */}
              <div className="text-center">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                  {settings?.name || 'Form Preview'}
                </h2>
                {settings?.description && (
                  <p className="text-gray-600 dark:text-gray-400">
                    {settings.description}
                  </p>
                )}
              </div>

              {/* Preview Notice */}
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                <div className="flex items-center">
                  <EyeIcon className="h-5 w-5 text-blue-500 mr-2" />
                  <div>
                    <p className="text-sm font-medium text-blue-800 dark:text-blue-200">
                      Preview Mode
                    </p>
                    <p className="text-xs text-blue-700 dark:text-blue-300">
                      This is how your form will look to users. Form submissions are disabled in preview.
                    </p>
                  </div>
                </div>
              </div>

              {/* Form Fields Preview */}
              <div className="space-y-4">
                {schema.fields.map((field) => {
                  // Mock field component for preview
                  return (
                    <div key={field.id} className="form-field-preview">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {field.label}
                        {field.required && (
                          <span className="text-red-500 ml-1">*</span>
                        )}
                      </label>
                      
                      {/* Field Preview based on type */}
                      {renderFieldPreview(field)}
                      
                      {field.settings?.helpText && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {field.settings.helpText}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Submit Button Preview */}
              <div className="flex justify-end">
                <button
                  type="button"
                  disabled
                  className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-purple-600 opacity-50 cursor-not-allowed"
                >
                  {settings?.submit_button_text || 'Submit'}
                </button>
              </div>

              {/* Conditional Rules Info */}
              {rules && rules.length > 0 && (
                <div className="mt-6 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                  <div className="flex items-start space-x-2">
                    <div className="flex-shrink-0 mt-0.5">
                      <svg className="h-4 w-4 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <p className="text-xs font-medium text-yellow-800 dark:text-yellow-200">
                        Conditional Logic Active
                      </p>
                      <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-1">
                        This form has {rules.length} conditional rule{rules.length !== 1 ? 's' : ''} that will show/hide fields based on user input. The preview shows all fields, but the live form will be dynamic.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Form Statistics */}
      <div className="grid grid-cols-3 gap-4 text-center">
        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
          <div className="text-lg font-semibold text-gray-900 dark:text-white">
            {schema.fields.length}
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400">
            Total Fields
          </div>
        </div>
        
        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
          <div className="text-lg font-semibold text-gray-900 dark:text-white">
            {schema.fields.filter(f => f.required).length}
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400">
            Required Fields
          </div>
        </div>
        
        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
          <div className="text-lg font-semibold text-gray-900 dark:text-white">
            {rules ? rules.length : 0}
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400">
            Logic Rules
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper function to render field previews
const renderFieldPreview = (field) => {
  const baseClassName = "w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-700 text-gray-500 dark:text-gray-400";
  
  switch (field.type) {
    case 'text':
    case 'email':
    case 'phone':
      return (
        <input
          type={field.type}
          placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}`}
          className={baseClassName}
          disabled
        />
      );
    
    case 'textarea':
      return (
        <textarea
          placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}`}
          rows={field.settings?.rows || 4}
          className={baseClassName}
          disabled
        />
      );
    
    case 'number':
      return (
        <input
          type="number"
          placeholder={field.placeholder || "0"}
          className={baseClassName}
          disabled
        />
      );
    
    case 'date':
      return (
        <input
          type="date"
          className={baseClassName}
          disabled
        />
      );
    
    case 'select':
      return (
        <select className={baseClassName} disabled>
          <option>Choose {field.label.toLowerCase()}</option>
          {field.options?.map((option, i) => (
            <option key={i} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      );
    
    case 'radio':
      return (
        <div className="space-y-2">
          {field.options?.map((option, i) => (
            <div key={i} className="flex items-center">
              <input
                type="radio"
                name={field.id}
                value={option.value}
                className="h-4 w-4 text-purple-600 border-gray-300 dark:border-gray-600"
                disabled
              />
              <label className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                {option.label}
              </label>
            </div>
          ))}
        </div>
      );
    
    case 'checkbox':
      return (
        <div className="space-y-2">
          {field.options?.map((option, i) => (
            <div key={i} className="flex items-center">
              <input
                type="checkbox"
                value={option.value}
                className="h-4 w-4 text-purple-600 border-gray-300 dark:border-gray-600 rounded"
                disabled
              />
              <label className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                {option.label}
              </label>
            </div>
          ))}
        </div>
      );
    
    case 'file':
      return (
        <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-4 text-center">
          <svg className="mx-auto h-8 w-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 48 48">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" />
          </svg>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
            Click to upload or drag and drop
          </p>
        </div>
      );
    
    case 'rating':
      return (
        <div className="flex items-center space-x-1">
          {[...Array(field.settings?.max || 5)].map((_, i) => (
            <svg key={i} className="h-6 w-6 text-gray-300" fill="currentColor" viewBox="0 0 20 20">
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
          ))}
        </div>
      );
    
    case 'slider':
      return (
        <div className="space-y-2">
          <input
            type="range"
            min={field.validation?.min || 0}
            max={field.validation?.max || 100}
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer"
            disabled
          />
          <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400">
            <span>{field.validation?.min || 0}</span>
            <span>{field.validation?.max || 100}</span>
          </div>
        </div>
      );
    
    case 'signature':
      return (
        <div className="border border-gray-300 dark:border-gray-600 rounded-lg p-4 h-32 bg-gray-50 dark:bg-gray-700 flex items-center justify-center">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Signature pad would appear here
          </p>
        </div>
      );
    
    default:
      return (
        <div className="text-sm text-gray-500 dark:text-gray-400 italic">
          Preview not available for field type: {field.type}
        </div>
      );
  }
};

export default FormPreview;